import { Icons } from '@components/Icons/Icons';
import { LoginForm } from '@components/LoginForm';
import { contentStyles, Modal, overlayStyles } from '@components/Modal/Modal';
import { TooltipWrapper } from '@components/Tooltip/Tooltip';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import * as Dialog from '@radix-ui/react-dialog';
import {
  type Dispatch,
  type ReactElement,
  type SetStateAction,
  useRef,
} from 'react';
import { twJoin } from 'tailwind-merge';

export function LoginModalUncontrolled({
  trigger,
  title = (
    <>
      Login or <TextLink href="/create-account">create an account</TextLink>
    </>
  ),
}: {
  trigger: ReactElement;
  title?: ReactElement;
}): ReactElement {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <Dialog.Root>
      <TooltipWrapper text="Login">
        <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      </TooltipWrapper>
      <Dialog.Portal>
        <Dialog.Overlay className={overlayStyles} id="overlay" />
        <Dialog.Content
          className={twJoin([
            contentStyles,
            'data-[state=open]:animate-contentShow max-h-[85vh] w-[90vw] max-w-[450px] p-[25px] shadow-[hsl(206_22%_7%/35%)_0px_10px_38px_-10px,hsl(206_22%_7%/20%)_0px_10px_20px_-15px] focus:outline-hidden',
            'rounded-2xl overflow-hidden',
          ])}
          onInteractOutside={(e) => {
            e.preventDefault();
            // password managers sit outside the dialog, so need to check if user has clicked outside the modal to close
            // or if password manager was clicked, then remain open
            if (e?.target && 'id' in e?.target && e.target.id === 'overlay') {
              closeButtonRef.current?.click();
            }
          }}
        >
          <div className="w-full">
            <Dialog.Title className="m-0 mb-6 text-lg font-medium text-grey-dark dark:text-white">
              {title}
            </Dialog.Title>
            <div className="my-4">
              <LoginForm onLogin={() => closeButtonRef.current?.click()} />
            </div>
          </div>
          <Dialog.Close asChild>
            <button
              type="button"
              aria-label="Close"
              ref={closeButtonRef}
              className="absolute right-2.5 top-2.5 border-none bg-transparent font-secondary text-4xl text-black hover:cursor-pointer hover:text-orange hover:no-underline focus:cursor-pointer focus:no-underline dark:text-white dark:hover:cursor-pointer dark:hover:text-orange dark:hover:no-underline dark:focus:cursor-pointer dark:focus:no-underline print:hidden"
            >
              <Icons.Close />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Login modal component.
 * @param modalTitle is the h2 to show at the top of the modal.
 * @param modalIntent is a string to show after "please log in or create an account ..." i.e. you could say "to finally meet that new years resolution" or something
 */
export function LoginModal({
  showModal,
  setShowModal,
  modalTitle,
  modalIntent,
}: {
  showModal: boolean;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  modalTitle: string;
  modalIntent?: string;
}): ReactElement {
  return (
    <Modal
      show={showModal}
      setShow={setShowModal}
      className="data-[state=open]:animate-contentShow max-h-[85vh] w-[90vw] max-w-[450px] p-[25px] shadow-[hsl(206_22%_7%/35%)_0px_10px_38px_-10px,hsl(206_22%_7%/20%)_0px_10px_20px_-15px] focus:outline-hidden rounded-2xl overflow-hidden"
    >
      <section className="max-w-[500px]">
        <h2 className="py-4">{modalTitle}</h2>
        {modalIntent && (
          <Paragraph>
            Log in or{' '}
            <TextLink href="/create-account">create an account</TextLink>{' '}
            {modalIntent}
          </Paragraph>
        )}
        <LoginForm
          onLogin={() => {
            setShowModal(false);
          }}
        />
      </section>
    </Modal>
  );
}
